import React, { useMemo } from 'react';

import { Combobox } from '@bestdoctor/spectrum-uikit/Combobox';
import { MultiCombobox } from '@bestdoctor/spectrum-uikit/MultiCombobox';
import type { ComboboxOption } from '@bestdoctor/spectrum-uikit/Combobox';
import { IListProgramProps, SearchParams } from 'interfaces/program/programInterfaces';
import { Box, Typography } from '@mui/material';
import { listProgramFilters, listProgramHeaderStyle, listProgramTitleStyle } from 'ui/programs/programs';

import { TableProgram } from './TableProgram/TableProgram';

const scopeService: readonly SearchParams[] = [
  { id: '1', title: 'Поликлиника', value: 'app' },
  { id: '2', title: 'Стоматология', value: 'stomatology' },
  { id: '3', title: 'Вызов врача на дом', value: 'home_doctor' },
  { id: '4', title: 'Скорая помощь', value: 'ambulance' },
  { id: '5', title: 'Плановая госпитализация', value: 'planned_hospitalization' },
  { id: '6', title: 'Экстренная госпитализация', value: 'emergency_hospitalization' },
];

export const ListProgram: React.FC<IListProgramProps> = ({
  programsQuantity,
  programsData,
  selectedProgramFilter,
  handleSelectedProgramFilter,
  searchByProgrammArr,
  searchByCityArr,
  selectedServicesForProgramsFilter,
  handleSelectedServices,
  selectedСityForProgram,
  handleSelectedСity,
  selectedPrograms,
  handleSelectedPrograms,
  handleActionsWithProgramsTooltip,
}) => {
  // Преобразуем программы в формат ComboboxOption
  const programOptions: ComboboxOption<SearchParams>[] = useMemo(
    () =>
      (searchByProgrammArr || []).map((program) => ({
        label: program.title,
        value: program,
      })),
    [searchByProgrammArr]
  );

  // Преобразуем города в формат ComboboxOption
  const cityOptions: ComboboxOption<SearchParams>[] = useMemo(
    () =>
      (searchByCityArr || []).map((city) => ({
        label: city.title,
        value: city,
      })),
    [searchByCityArr]
  );

  // Преобразуем услуги в формат ComboboxOption
  const serviceOptions: ComboboxOption<SearchParams>[] = useMemo(
    () =>
      scopeService.map((service) => ({
        label: service.title,
        value: service,
      })),
    []
  );

  // Находим текущую выбранную опцию программы
  const selectedProgramOption = useMemo(
    () => programOptions.find((option) => option.value.id === selectedProgramFilter?.id) || null,
    [programOptions, selectedProgramFilter]
  );

  // Находим текущую выбранную опцию города
  const selectedCityOption = useMemo(
    () => cityOptions.find((option) => option.value.id === selectedСityForProgram?.id) || null,
    [cityOptions, selectedСityForProgram]
  );

  // Находим текущие выбранные опции услуг
  const selectedServiceOptions = useMemo(
    () =>
      serviceOptions.filter((option) =>
        selectedServicesForProgramsFilter.some((service) => service.id === option.value.id)
      ),
    [serviceOptions, selectedServicesForProgramsFilter]
  );

  return (
    <Box component="section" sx={{ marginTop: '32px' }}>
      <Box sx={listProgramHeaderStyle}>
        <Typography variant="h4" sx={() => listProgramTitleStyle(false)}>
          Программы обслуживания
          {programsQuantity !== 0 && (
            <Box component="span" sx={{ color: '#bcbec8', margin: '0 8px' }}>
              (всего: {programsQuantity})
            </Box>
          )}
        </Typography>
      </Box>
      <Box sx={listProgramFilters}>
        <Box sx={{ width: '100%', flex: '1 1 0' }}>
          <Combobox<SearchParams>
            label="Программа"
            placeholder="Программа"
            options={programOptions}
            value={selectedProgramOption?.value}
            searchable
            clearable
            onChange={(value, option) => {
              if (value && option) {
                sessionStorage.setItem('tableProgramsFilterProgram', JSON.stringify(value));
                handleSelectedProgramFilter(value);
              } else {
                sessionStorage.setItem('tableProgramsFilterProgram', JSON.stringify(''));
                handleSelectedProgramFilter(null);
              }
            }}
          />
        </Box>

        <Box sx={{ width: '100%', flex: '1 1 0' }}>
          <MultiCombobox<SearchParams>
            label="Услуга"
            placeholder="Услуга"
            options={serviceOptions}
            value={selectedServiceOptions.map((opt) => opt.value)}
            searchable
            clearable
            onChange={(values) => {
              sessionStorage.setItem('tableProgramsFilterServices', JSON.stringify(values || ''));
              handleSelectedServices(values || []);
            }}
          />
        </Box>

        <Box sx={{ width: '100%', flex: '1 1 0' }}>
          <Combobox<SearchParams>
            label="Город"
            placeholder="Город"
            options={cityOptions}
            value={selectedCityOption?.value}
            searchable
            clearable
            onChange={(value, option) => {
              if (value && option) {
                sessionStorage.setItem('tableProgramsFilterCity', JSON.stringify(value));
                handleSelectedСity(value);
              } else {
                sessionStorage.setItem('tableProgramsFilterCity', JSON.stringify(''));
                handleSelectedСity(null);
              }
            }}
          />
        </Box>
      </Box>

      <TableProgram
        programsData={programsData}
        selectedPrograms={selectedPrograms}
        handleSelectedPrograms={handleSelectedPrograms}
        handleActionsWithProgramsTooltip={handleActionsWithProgramsTooltip}
      />
    </Box>
  );
};
